import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NormalizedPrediction, NormalizedScheduledGame, NormalizedWithheldGame } from "../domains/predictions/types";
import { usePredictions } from "../domains/predictions/hooks/usePredictions";
import { GameGrid } from "../domains/predictions/components/GameGrid";
import { WithheldGameCard } from "../domains/predictions/components/WithheldGameCard";
import { ScheduledGameCard } from "../domains/predictions/components/ScheduledGameCard";
import { StatePanel } from "../shared/components/StatePanel";
import { formatRelativeTime, formatLastUpdatedEt } from "../shared/utils/formatters";
import { Logo } from "../shared/components/Logo";
import { LogoShield } from "../shared/components/LogoShield";
import { Logo8Ball } from "../shared/components/Logo8Ball";
import { LogoCrosshair } from "../shared/components/LogoCrosshair";
import { LogoBullseye } from "../shared/components/LogoBullseye";
import { LogoChart } from "../shared/components/LogoChart";
import { LogoGauge } from "../shared/components/LogoGauge";
import { LogoRadar } from "../shared/components/LogoRadar";

/* ──────────────────────────── Logo picker ──────────────────────── */

/**
 * Switch which logo renders in the header.
 * Change ACTIVE_LOGO to preview different options:
 *
 *   "original"   — Rounded-rect Q with data dot          ← ACTIVE
 *   "shield"     — Shield silhouette with Q cutout
 *   "8ball"      — Magic 8-ball with Q in the window
 *   "crosshair"  — Precision crosshair with Q target     ← BACKUP
 *   "bullseye"   — Concentric target rings, Q at centre
 *   "chart"      — Upward chart line, Q as peak data point
 *   "gauge"      — Precision dial/gauge, needle at high accuracy
 *   "radar"      — Radar screen with sweep line, blips, Q as detection ring
 */
type LogoVariant = "original" | "shield" | "8ball" | "crosshair" | "bullseye" | "chart" | "gauge" | "radar";
const ACTIVE_LOGO: LogoVariant = "original";
const SHOW_STALE_WARNING = false;

function ActiveLogo({ size, className }: { size: number; className?: string }) {
  switch (ACTIVE_LOGO) {
    case "shield":
      return <LogoShield size={size} className={className} />;
    case "8ball":
      return <Logo8Ball size={size} className={className} />;
    case "crosshair":
      return <LogoCrosshair size={size} className={className} />;
    case "bullseye":
      return <LogoBullseye size={size} className={className} />;
    case "chart":
      return <LogoChart size={size} className={className} />;
    case "gauge":
      return <LogoGauge size={size} className={className} />;
    case "radar":
      return <LogoRadar size={size} className={className} />;
    default:
      return <Logo size={size} className={className} />;
  }
}

/* ──────────────────────────── Sorting ──────────────────────────── */

type SortOption = "time_asc" | "time_desc" | "confidence_desc" | "confidence_asc";
const EASTERN_TIMEZONE = "America/New_York";

function getEasternDateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    return "";
  }

  return `${year}-${month}-${day}`;
}

function getEasternDateKeyFromUtc(utcTimestamp: string | null | undefined): string | null {
  if (!utcTimestamp) {
    return null;
  }

  const parsed = new Date(utcTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return getEasternDateKey(parsed) || null;
}

function normalizeIsoDate(dateKey: string | null | undefined): string | null {
  if (!dateKey) {
    return null;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? dateKey : null;
}

function getPredictionEtDateKey(prediction: NormalizedPrediction): string {
  return normalizeIsoDate(prediction.gameDate) ?? getEasternDateKeyFromUtc(prediction.gameStartTimeUtc) ?? "";
}

function getWithheldEtDateKey(game: { gameDate: string; gameStartTimeUtc: string }): string {
  return normalizeIsoDate(game.gameDate) ?? getEasternDateKeyFromUtc(game.gameStartTimeUtc) ?? "";
}

function getScheduledEtDateKey(game: NormalizedScheduledGame): string {
  return normalizeIsoDate(game.gameDate) ?? getEasternDateKeyFromUtc(game.gameStartTimeUtc) ?? "";
}

function toSearchableScheduled(game: NormalizedScheduledGame): string {
  return `${game.awayTeam ?? ""} ${game.homeTeam ?? ""} ${game.statusDetailed} ${game.gameId}`.toLowerCase();
}

function formatEtDateLabel(dateKey: string): string {
  const parts = dateKey.split("-").map((value) => Number(value));
  if (parts.length !== 3 || parts.some((value) => Number.isNaN(value))) {
    return dateKey;
  }

  const [year, month, day] = parts;
  const parsed = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
}

function getGameTimeValue(p: NormalizedPrediction): number {
  const parsed = p.gameStartTimeUtc ? new Date(p.gameStartTimeUtc).getTime() : Number.NaN;
  if (!Number.isNaN(parsed)) return parsed;
  const fallback = new Date(`${p.gameDate}T00:00:00Z`).getTime();
  return Number.isNaN(fallback) ? 0 : fallback;
}

/** Map game status to a sort priority: Live (0) > Upcoming (1) > Final (2) > Postponed (3). */
function getStatusPriority(p: NormalizedPrediction): number {
  const status = (p.gameStatus ?? "").toLowerCase();
  const detailed = (p.statusDetailed ?? "").toLowerCase();

  if (status === "live" || detailed === "in progress" || detailed === "warmup") return 0;
  if (status === "final" || detailed === "final" || detailed === "game over" || detailed === "completed early") return 2;
  if (detailed === "postponed" || detailed === "cancelled" || detailed === "suspended") return 3;
  return 1; // upcoming / preview
}

function sortPredictions(list: NormalizedPrediction[], by: SortOption): NormalizedPrediction[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    // Primary sort: status priority (Live first, then Upcoming, then Final)
    const statusDiff = getStatusPriority(a) - getStatusPriority(b);
    if (statusDiff !== 0) return statusDiff;

    // Secondary sort: user-selected criteria
    switch (by) {
      case "time_asc":
        return getGameTimeValue(a) - getGameTimeValue(b);
      case "time_desc":
        return getGameTimeValue(b) - getGameTimeValue(a);
      case "confidence_desc":
        return (b.finalConfidence ?? -1) - (a.finalConfidence ?? -1);
      case "confidence_asc":
        return (a.finalConfidence ?? -1) - (b.finalConfidence ?? -1);
    }
  });
  return sorted;
}

/* ──────────────────────────── Search ──────────────────────────── */

/**
 * Builds a lowercase searchable string from team names and location labels.
 * Supports searching by team name, city, state, or market.
 */
function toSearchable(p: NormalizedPrediction): string {
  return `${p.homeTeam} ${p.awayTeam} ${p.homeTeamLocation ?? ""} ${p.awayTeamLocation ?? ""} ${p.homeTeamCity ?? ""} ${p.awayTeamCity ?? ""}`.toLowerCase();
}

function toSearchableWithheld(game: {
  gameId: number;
  homeTeam: string | null;
  awayTeam: string | null;
  statusDetailed: string;
}): string {
  return `${game.awayTeam ?? ""} ${game.homeTeam ?? ""} ${game.statusDetailed} ${game.gameId}`.toLowerCase();
}

function getWithheldTimeValue(game: { gameStartTimeUtc: string }): number {
  const parsed = new Date(game.gameStartTimeUtc).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function sortWithheldGames<T extends { gameStartTimeUtc: string }>(list: T[], by: SortOption): T[] {
  const sorted = [...list];

  if (by === "time_desc") {
    sorted.sort((a, b) => getWithheldTimeValue(b) - getWithheldTimeValue(a));
    return sorted;
  }

  sorted.sort((a, b) => getWithheldTimeValue(a) - getWithheldTimeValue(b));
  return sorted;
}

/* ──────────────────────────── App ──────────────────────────── */

interface AppProps {
  accountEmail?: string | null;
  onSignOut?: () => void;
}

export function App({ accountEmail = null, onSignOut }: AppProps): JSX.Element {
  const { data, loading, error, stale, partial } = usePredictions();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("time_asc");
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountEmailLabel = accountEmail && accountEmail.trim() ? accountEmail : "Signed in";
  const preferredRunDate = normalizeIsoDate(data?.runDate ?? null);

  // Close account dropdown on outside click
  useEffect(() => {
    if (!accountOpen) return;
    function handleClick(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [accountOpen]);

  // Close on Escape key
  const handleAccountKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") setAccountOpen(false);
    },
    []
  );

  const etDateOptions = useMemo(() => {
    if (!data) {
      return [] as string[];
    }

    const keys = new Set<string>();
    for (const prediction of data.predictions) {
      const key = getPredictionEtDateKey(prediction);
      if (key) {
        keys.add(key);
      }
    }

    for (const game of data.withheldGames) {
      const key = getWithheldEtDateKey(game);
      if (key) {
        keys.add(key);
      }
    }

    for (const game of data.scheduledGames) {
      const key = getScheduledEtDateKey(game);
      if (key) {
        keys.add(key);
      }
    }

    return [...keys].sort((a, b) => a.localeCompare(b));
  }, [data]);

  const resolvedEtDate = useMemo(() => {
    if (etDateOptions.length === 0) {
      return "";
    }
    if (preferredRunDate && etDateOptions.includes(preferredRunDate)) {
      return preferredRunDate;
    }
    return etDateOptions[0];
  }, [etDateOptions, preferredRunDate]);

  const {
    visiblePredictions,
    visibleWithheldGames,
    visibleScheduledGames,
    scopedPredictionsCount,
    scopedWithheldCount,
    scopedScheduledCount,
  } = useMemo(() => {
    if (!data) {
      return {
        visiblePredictions: [],
        visibleWithheldGames: [],
        visibleScheduledGames: [],
        scopedPredictionsCount: 0,
        scopedWithheldCount: 0,
        scopedScheduledCount: 0,
      };
    }

    const scopedPredictions = resolvedEtDate
      ? data.predictions.filter((prediction) => getPredictionEtDateKey(prediction) === resolvedEtDate)
      : data.predictions;
    const scopedWithheldGames = resolvedEtDate
      ? data.withheldGames.filter((game) => getWithheldEtDateKey(game) === resolvedEtDate)
      : data.withheldGames;
    const scopedScheduledGames = resolvedEtDate
      ? data.scheduledGames.filter((game) => getScheduledEtDateKey(game) === resolvedEtDate)
      : data.scheduledGames;

    const query = searchTerm.trim().toLowerCase();
    const filteredPredictions = query
      ? scopedPredictions.filter((p) => toSearchable(p).includes(query))
      : scopedPredictions;
    const filteredWithheldGames = query
      ? scopedWithheldGames.filter((game) => toSearchableWithheld(game).includes(query))
      : scopedWithheldGames;
    const filteredScheduledGames = query
      ? scopedScheduledGames.filter((game) => toSearchableScheduled(game).includes(query))
      : scopedScheduledGames;

    return {
      visiblePredictions: sortPredictions(filteredPredictions, sortBy),
      visibleWithheldGames: sortWithheldGames(filteredWithheldGames, sortBy),
      visibleScheduledGames: sortWithheldGames(filteredScheduledGames, sortBy),
      scopedPredictionsCount: scopedPredictions.length,
      scopedWithheldCount: scopedWithheldGames.length,
      scopedScheduledCount: scopedScheduledGames.length,
    };
  }, [data, resolvedEtDate, searchTerm, sortBy]);

  const hasAnyData = !!data && (data.predictions.length > 0 || data.withheldGames.length > 0 || data.scheduledGames.length > 0);
  const hasScopedData = scopedPredictionsCount + scopedWithheldCount + scopedScheduledCount > 0;
  const hasOnlyWithheldData = !!data && scopedPredictionsCount === 0 && (scopedWithheldCount > 0 || scopedScheduledCount > 0);
  const hasNoSearchResults =
    !!data &&
    hasScopedData &&
    visiblePredictions.length + visibleWithheldGames.length + visibleScheduledGames.length === 0;
  const selectedEtDateLabel = resolvedEtDate ? formatEtDateLabel(resolvedEtDate) : null;
  const scopedGameCount = scopedPredictionsCount + scopedWithheldCount + scopedScheduledCount;

  return (
    <div className="app-shell">
      {/* ── Sticky header ── */}
      <header className="app-header">
        <div className="header-inner">
          <a
            href="#top"
            className="brand"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            aria-label="Back to top"
          >
            <ActiveLogo size={36} className="brand-logo" />
            <div className="brand-text">
              <h1 className="brand-name">Qpicks<span className="brand-dot">.io</span></h1>
              <p className="brand-tagline">Even The Odds</p>
            </div>
          </a>

          {/* Account dropdown */}
          <div className="account-menu" ref={accountRef} onKeyDown={handleAccountKeyDown}>
            <button
              type="button"
              className="account-trigger"
              onClick={() => setAccountOpen((prev) => !prev)}
              aria-expanded={accountOpen}
              aria-haspopup="true"
              aria-label="Account menu"
            >
              <span className="account-avatar" aria-hidden="true">Q</span>
              <svg className="account-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {accountOpen && (
              <div className="account-dropdown" role="menu">
                <div className="account-info">
                  <span className="account-name">My Account</span>
                  <span className="account-email">{accountEmailLabel}</span>
                </div>
                <div className="account-divider" />
                <button
                  type="button"
                  className="account-signout"
                  role="menuitem"
                  onClick={onSignOut}
                  disabled={!onSignOut}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Main content area ── */}
      <main className="main-content">
        {/* Hero section */}
        <section className="hero">
          <p className="hero-kicker"><span className="hero-kicker-line" aria-hidden="true" /> Elite MLB Intelligence</p>
          <h2 className="hero-title">Today&apos;s <span className="hero-title-accent">Predictions</span></h2>
          <p className="hero-subtitle">
            AI-powered picks backed by real data — pitching matchups, batting
            trends, and calibrated win probabilities so you can bet smarter.
          </p>
          {hasAnyData && data && (
            <p className="hero-context">
              <span className="hero-context-dot" aria-hidden="true" />
              <span>
                <span className="hero-context-count">
                  {scopedGameCount} game
                  {scopedGameCount !== 1 ? "s" : ""}
                </span>
                {" · "}
                {selectedEtDateLabel ? `${selectedEtDateLabel} official slate` : "Official slate unavailable"}
              </span>
            </p>
          )}
          <div className="hero-gradient-bar" aria-hidden="true" />
        </section>

        {/* Search + sort controls — always visible when there's data */}
        {!loading && !error && hasScopedData && (
          <div className="controls-bar">
            <div className="search-field">
              <label htmlFor="game-search" className="sr-only">Search by team, city, state, or market</label>
              <svg className="search-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input
                id="game-search"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search team, city, or state…"
                className="search-input"
                autoComplete="off"
              />
            </div>

            <div className="sort-field">
              <label htmlFor="game-sort" className="sr-only">Sort games</label>
              <select
                id="game-sort"
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
              >
                <option value="time_asc">Game Time ↑</option>
                <option value="time_desc">Game Time ↓</option>
                <option value="confidence_desc">Confidence ↑</option>
                <option value="confidence_asc">Confidence ↓</option>
              </select>
            </div>
          </div>
        )}

        {/* State banners */}
        {!loading && !error && partial && (
          <StatePanel
            variant="warning"
            title="Partial data"
            message="Some predictions may be missing data. Values shown reflect what is currently available."
          />
        )}

        {SHOW_STALE_WARNING && !loading && !error && stale && !partial && (
          <div className="freshness-banner" role="status">
            <div className="freshness-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 4.5V8.5L10.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="freshness-content">
              <span className="freshness-age">
                Updated {formatRelativeTime(data?.lastUpdatedAt ?? null) ?? "at an unknown time"}
              </span>
              <span className="freshness-detail">
                {data?.lastUpdatedAt
                  ? `Last refresh: ${formatLastUpdatedEt(data.lastUpdatedAt)} ET · Predictions update every 15 min`
                  : "Unable to verify when predictions were last refreshed"}
              </span>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <section className="skeleton-grid" aria-label="Loading predictions">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-card" aria-hidden="true">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-subtitle" />
                <div className="skeleton-line skeleton-body" />
                <div className="skeleton-line skeleton-short" />
                <div className="skeleton-chips">
                  <div className="skeleton-chip" />
                  <div className="skeleton-chip" />
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Error state */}
        {!loading && error && (
          <StatePanel
            variant="error"
            title="Predictions unavailable"
            message="Could not load prediction data. Please try again shortly."
          />
        )}

        {/* Empty state */}
        {!loading && !error && hasOnlyWithheldData && (
          <StatePanel
            variant="info"
            title="Predictions Are Pending Official Updates"
            message="No games are publishable yet because official lineups or named starters are still being finalized."
          />
        )}

        {/* Empty state */}
        {!loading && !error && data && scopedPredictionsCount === 0 && scopedWithheldCount === 0 && scopedScheduledCount === 0 && (
          <StatePanel
            title="No predictions yet"
            message="No games currently have published predictions. Check back closer to game time."
          />
        )}

        {/* No search results */}
        {!loading && !error && hasNoSearchResults && (
          <StatePanel
            title="No matching games"
            message={`No games match "${searchTerm}". Try a different team, city, or state name.`}
          />
        )}

        {/* Published predictions */}
        {!loading && !error && visiblePredictions.length > 0 && (
          <section className="published-section" aria-label="Published predictions">
            <GameGrid
              predictions={visiblePredictions}
            />
          </section>
        )}

        {/* Pending games — withheld + scheduled, unified in a single grid */}
        {!loading && !error && (visibleWithheldGames.length > 0 || visibleScheduledGames.length > 0) && (
          <section className="pending-section" aria-label="Pending games">
            <div className="pending-section-header">
              <h3 className="pending-section-title">Other Games</h3>
              <p className="pending-section-subtitle">
                {visibleWithheldGames.length + visibleScheduledGames.length} game{visibleWithheldGames.length + visibleScheduledGames.length !== 1 ? "s" : ""} not yet eligible for prediction.
              </p>
            </div>
            <div className="withheld-grid" role="list" aria-label="Pending games list">
              {(() => {
                type UnifiedPending =
                  | { kind: "withheld"; game: NormalizedWithheldGame }
                  | { kind: "scheduled"; game: NormalizedScheduledGame };

                const unified: UnifiedPending[] = [
                  ...visibleWithheldGames.map((g): UnifiedPending => ({ kind: "withheld", game: g })),
                  ...visibleScheduledGames.map((g): UnifiedPending => ({ kind: "scheduled", game: g })),
                ].sort((a, b) => {
                  const aMs = new Date(a.game.gameStartTimeUtc).getTime();
                  const bMs = new Date(b.game.gameStartTimeUtc).getTime();
                  if (Number.isNaN(aMs) && Number.isNaN(bMs)) return 0;
                  if (Number.isNaN(aMs)) return 1;
                  if (Number.isNaN(bMs)) return -1;
                  return aMs - bMs;
                });

                return unified.map((entry) => (
                  <div role="listitem" key={entry.game.gameId}>
                    {entry.kind === "withheld" ? (
                      <WithheldGameCard game={entry.game} />
                    ) : (
                      <ScheduledGameCard game={entry.game} />
                    )}
                  </div>
                ));
              })()}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <p>
          Predictions are probabilistic estimates, not guarantees.
          Always gamble responsibly.
        </p>
        <p className="footer-brand">© {new Date().getFullYear()} Qpicks.io</p>
      </footer>
    </div>
  );
}
